import { FC } from 'react';

import { IconPropertyFilter } from './IconPropertyFilter';
import { NumberRangeFilter } from './NumberRangeFilter';
import { PropertyFilterSelect } from './PropertyFilterSelect';
import { PropertyOptionIcon } from './PropertyOptionIcon';
import { ProductFilter } from '../../../types';
import { cn } from '../../../utils';

interface Props {
    readonly property: ProductFilter;
    readonly values: Record<string, string>;
    readonly onChange: (
        updates: Record<string, string>,
        preserveState?: boolean,
    ) => void;
    readonly size?: 'default' | 'compact';
    readonly triggerClassName?: string;
    readonly contentClassName?: string;
}

const ALL_VALUE = 'all';

export const ConfiguredPropertyFilter: FC<Props> = ({
    property,
    values,
    onChange,
    size = 'default',
    triggerClassName,
    contentClassName,
}) => {
    const value = values[property.slug] || ALL_VALUE;
    const settings = property.filter_settings ?? {};
    const testId = `property-filter-${property.slug}`;

    const toggleQuickOption = (slug: string) => {
        onChange(
            { [property.slug]: value === slug ? ALL_VALUE : slug },
            Boolean(property.multiselect),
        );
    };

    const quickOptions = settings.quick_options ?? [];
    const quickOptionButtons = quickOptions.length > 0 && (
        <div className="mb-2 space-y-2">
            {quickOptions.map((option) => {
                const selected = value
                    .split(',')
                    .map((item) => item.trim())
                    .includes(option.slug);

                return (
                    <button
                        key={option.slug}
                        type="button"
                        aria-pressed={selected}
                        data-testid={`${testId}-quick-${option.slug}`}
                        onClick={() => toggleQuickOption(option.slug)}
                        className={cn(
                            'flex w-full items-center gap-2 rounded-xl border px-3 py-2 text-left text-sm transition-colors hover:bg-muted/60',
                            selected &&
                                'border-primary bg-primary/10 text-primary',
                        )}
                    >
                        <PropertyOptionIcon
                            iconKey={option.icon_key}
                            label={option.label}
                        />
                        <span>{option.label}</span>
                    </button>
                );
            })}
        </div>
    );

    let control;

    switch (property.filter_control) {
        case 'icon_multi_select':
            control = (
                <IconPropertyFilter
                    value={value}
                    options={property.product_property_options ?? []}
                    onChange={(nextValue) =>
                        onChange(
                            { [property.slug]: nextValue },
                            Boolean(property.multiselect),
                        )
                    }
                    testId={testId}
                />
            );
            break;
        case 'toggle': {
            const option = settings.toggle_option;

            control = option ? (
                <button
                    type="button"
                    aria-pressed={value === option.slug}
                    data-testid={`${testId}-toggle`}
                    onClick={() => toggleQuickOption(option.slug)}
                    className={cn(
                        'flex w-full items-center gap-2 rounded-xl border px-3 py-2 text-left text-sm transition-colors hover:bg-muted/60',
                        value === option.slug &&
                            'border-primary bg-primary/10 text-primary',
                    )}
                >
                    <PropertyOptionIcon
                        iconKey={option.icon_key}
                        label={option.label}
                    />
                    <span>{option.label}</span>
                </button>
            ) : null;
            break;
        }
        case 'number_range':
            control = (
                <NumberRangeFilter
                    minimum={values[`${property.slug}_min`]}
                    maximum={values[`${property.slug}_max`]}
                    minPlaceholder={settings.min_placeholder}
                    maxPlaceholder={settings.max_placeholder}
                    onChange={(minimum, maximum) =>
                        onChange({
                            [`${property.slug}_min`]: minimum,
                            [`${property.slug}_max`]: maximum,
                        })
                    }
                    testId={testId}
                />
            );
            break;
        default:
            control = (
                <PropertyFilterSelect
                    value={value}
                    onChange={(nextValue) =>
                        onChange(
                            { [property.slug]: nextValue },
                            Boolean(property.multiselect),
                        )
                    }
                    options={property.product_property_options ?? []}
                    allLabel={settings.all_label ?? `Усі: ${property.name}`}
                    searchable={property.searchable}
                    multiselect={property.multiselect}
                    size={size}
                    testId={testId}
                    triggerClassName={triggerClassName}
                    contentClassName={contentClassName}
                />
            );
    }

    return (
        <>
            {quickOptionButtons}
            {control}
            {settings.helper_text && (
                <p className="mt-2 text-xs leading-5 text-muted-foreground">
                    {settings.helper_text}
                </p>
            )}
        </>
    );
};
